export type HighlightSegment = { text: string; isMatch: boolean };

// Lowercase and strip accents, keeping each folded char's index in `text`, so a
// match found in the folded text maps back onto the original characters.
const fold = (text: string): { folded: string; origin: number[] } => {
  let folded = '';
  const origin: number[] = [];
  let index = 0;
  for (const char of text) {
    const base = char
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    for (const foldedChar of base) {
      folded += foldedChar;
      origin.push(index);
    }
    index += char.length;
  }
  return { folded, origin };
};

const findAll = (text: string, needle: string): [number, number][] => {
  const ranges: [number, number][] = [];
  let position = text.indexOf(needle);
  while (position >= 0) {
    ranges.push([position, position + needle.length]);
    position = text.indexOf(needle, position + needle.length);
  }
  return ranges;
};

// Split a search snippet into marked and unmarked parts. Mirrors the backend's
// literal match (libs/knowledge/keyword_hits.py): case- and accent-insensitive,
// the whole phrase when present, otherwise each query word.
export const highlightMatches = (
  text: string,
  query: string
): HighlightSegment[] => {
  const words = fold(query).folded.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [{ text, isMatch: false }];

  const { folded, origin } = fold(text);
  let ranges = findAll(folded, words.join(' '));
  if (ranges.length === 0) {
    ranges = words.flatMap(word => findAll(folded, word));
  }
  if (ranges.length === 0) return [{ text, isMatch: false }];

  // Merge overlapping word ranges, then map folded indexes back to `text`
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const [start, end] of ranges) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) {
      last[1] = Math.max(last[1], end);
    } else {
      merged.push([start, end]);
    }
  }

  const mapped = new Set(origin);
  const segments: HighlightSegment[] = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    const matchStart = origin[start];
    let matchEnd = origin[end - 1] + 1;
    // Keep the rest of the last matched character (accents, surrogate pairs)
    while (matchEnd < text.length && !mapped.has(matchEnd)) {
      matchEnd += 1;
    }
    if (matchStart > cursor) {
      segments.push({ text: text.slice(cursor, matchStart), isMatch: false });
    }
    segments.push({ text: text.slice(matchStart, matchEnd), isMatch: true });
    cursor = matchEnd;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), isMatch: false });
  }
  return segments;
};
